import React from 'react';
import Highlighter from 'react-highlight-words';
import { Link } from 'react-router-dom';
import cs from 'classnames';
import { Typography } from '@forgeglobal/design-framework';
import { Scrollbars } from 'react-custom-scrollbars';
import { Persona } from '@tcs/client-portal-api/types/Persona';
import { SearchType } from '@tcs/client-portal-api/types/SearchType';
import { useStyles } from './style';
import { SearchPanelProps, RecentItemType } from './types';

const MAX_LENGTH = 10;

const categoriesDictionary = {
  [Persona.Representative]: {
    recent: 'Recently viewed representatives',
    current: 'Representatives',
    order: 2,
    searchType: SearchType.Representative,
  },
  [Persona.Individual]: {
    recent: 'Recently viewed accounts',
    current: 'Account Holders',
    order: 1,
    searchType: SearchType.Account,
  },
};

const SearchPanel = ({
  items,
  isRecent,
  onSelect,
  onClose,
  searchedValue,
  onSearch,
}: SearchPanelProps) => {
  const classes = useStyles();

  const getCategoryName = (category: string) => {
    if (!categoriesDictionary[category]) return;
    return categoriesDictionary[category][isRecent ? 'recent' : 'current'];
  };

  const onLinkClick = () => {
    onClose();
    onSearch('');
  };

  const renderViewAllLink = (category: string) => (
    <Link
      to={{
        pathname: '/results',
      }}
      state={{
        searchValue: searchedValue,
        searchType: categoriesDictionary[category]?.searchType,
      }}
      onClick={onLinkClick}
      className={cs(classes.item, classes.viewAll)}>
      <Typography variant="body1" color="primary">
        View all results
      </Typography>
    </Link>
  );

  const renderItems = (list: RecentItemType[], persona: string) =>
    list.map((item) => (
      <div
        className={classes.item}
        onClick={onSelect(item, persona)}
        key={item.id}>
        <div className={classes.itemName}>
          <Typography variant="body1" className={classes.name}>
            <Highlighter
              className={classes.name}
              searchWords={[searchedValue]}
              textToHighlight={item.name}
              highlightClassName={classes.highlighted}
              highlightTag="span"
            />
          </Typography>
        </div>
        <div className={classes.itemInfo}>
          {item.type && (
            <div className={classes.itemType}>
              <Typography variant="body1" color="textSecondary">
                {item.type}
              </Typography>
            </div>
          )}
          <div>
            <Typography variant="body1" className={classes.itemNumber}>
              <Highlighter
                searchWords={[searchedValue]}
                textToHighlight={item.id}
                highlightClassName={classes.highlighted}
                highlightTag="span"
              />
            </Typography>
          </div>
        </div>
      </div>
    ));

  const renderCategories = (list: typeof items) =>
    Object.keys(list)
      .sort(
        (a, b) => categoriesDictionary[a].order - categoriesDictionary[b].order
      )
      .map((category) => (
        <div
          key={category}
          className={classes.category}
          data-test-id={`results-container-${category}`}>
          <div className={classes.categoryHeader}>
            <Typography variant="overline" color="textSecondary">
              {getCategoryName(category)}
            </Typography>
          </div>
          <div>
            {renderItems(items[category].data.slice(0, MAX_LENGTH), category)}
            {items[category].data.length >= MAX_LENGTH &&
              searchedValue &&
              renderViewAllLink(category)}
          </div>
        </div>
      ));

  return (
    /* @ts-ignore */
    <Scrollbars autoHeight autoHeightMax={540}>
      {renderCategories(items)}
    </Scrollbars>
  );
};

export default SearchPanel;
